/*
 * B2B cart progress bar.
 *
 * Reads the wholesale rules straight from the BSS B2B Solution app instead of
 * mirroring them in theme settings, so adding a collection to a rule in the app
 * needs no theme change.
 *
 * The rules are only reachable client side: BSS stores them in app-owned
 * metafields, which Liquid cannot read, and its app embed block inlines them
 * into the page as `window.BSS_B2B.configData` (custom pricing) and
 * `window.BSS_B2B.amoRules` (minimum order). Both are server rendered into the
 * HTML, so they are already there when this script runs.
 *
 * Progressive enhancement: the theme renders its regular free shipping bar in
 * Liquid. This script only rewrites it when the shopper actually matches a
 * wholesale rule. If BSS is missing or the shopper is retail, the Liquid output
 * stays untouched.
 */
(function () {
  'use strict';

  var AMO_TYPE_AMOUNT = 0;
  var AMO_TYPE_QUANTITY = 1;

  /* ---------------------------------------------------------------- utils */

  function toIdSet(value) {
    var set = Object.create(null);
    String(value == null ? '' : value)
      .split(',')
      .forEach(function (id) {
        var trimmed = id.trim();
        if (trimmed) set[trimmed] = true;
      });
    return set;
  }

  function tagsMatch(ruleTags, customerTags) {
    if (!ruleTags) return false;
    return String(ruleTags)
      .split(',')
      .some(function (tag) {
        var needle = tag.trim().toLowerCase();
        return needle !== '' && customerTags.indexOf(needle) !== -1;
      });
  }

  /**
   * Shopify money formatter. Mirrors the `money` filter so the JS output is
   * indistinguishable from the amounts Liquid renders elsewhere in the cart.
   */
  function formatMoney(cents, format) {
    var value = Math.round(Number(cents) || 0);
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var name = match ? match[1] : 'amount';

    function withDelimiters(precision, thousands, decimal) {
      var fixed = (value / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';
      return whole + fraction;
    }

    var formatted;
    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(2, "'", '.');
        break;
      default:
        formatted = withDelimiters(2, ',', '.');
    }
    return format.replace(placeholder, formatted);
  }

  // Liquid hands over its translations with %%token%% placeholders already in
  // place, because `{{ }}` inside a `t` filter argument breaks Liquid parsing.
  function interpolate(template, values) {
    return template.replace(/%%(\w+)%%/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  /* --------------------------------------------------- last added tracking */

  // The lead row follows the product the shopper most recently added (client
  // spec). The add usually happens on the PDP, a separate page load from the
  // cart, and the cart context carries no add-order timestamp — so the added
  // variant id is stashed in sessionStorage on `eurus:product:added` and read
  // back when the bar renders. sessionStorage (not local) so it clears with
  // the tab, roughly matching how long a cart session feels current.
  var LAST_ADDED_KEY = 'themeB2BLastAddedVariant';

  function rememberLastAdded(detail) {
    var payload = detail && detail.product;
    if (!payload) return;
    var item = Array.isArray(payload)
      ? payload[payload.length - 1]
      : Array.isArray(payload.items)
        ? payload.items[payload.items.length - 1]
        : payload;
    if (!item) return;
    var id = item.variant_id || item.id;
    if (id == null) return;
    try {
      window.sessionStorage.setItem(LAST_ADDED_KEY, String(id));
    } catch (error) {
      /* private mode / storage disabled — the bar just keeps its default order */
    }
  }

  function readLastAdded() {
    try {
      return window.sessionStorage.getItem(LAST_ADDED_KEY);
    } catch (error) {
      return null;
    }
  }

  /* ------------------------------------------------------------ rule read */

  function bssRoot() {
    return window.BSS_B2B || window.BSS_B2B_CLONE || null;
  }

  /**
   * Minimum order rules ("Advanced Minimum Order" in BSS).
   * type 0 = minimum cart amount, type 1 = minimum item quantity.
   */
  function readMinimumRules(customerTags) {
    var root = bssRoot();
    var raw =
      (root && root.amoRules) ||
      (window.BSS_B2B_CLONE && window.BSS_B2B_CLONE.amoRules) ||
      [];
    if (!Array.isArray(raw)) return [];

    return raw
      .filter(function (rule) {
        return tagsMatch(rule.customer_tags, customerTags);
      })
      .map(function (rule) {
        return {
          id: rule.id,
          name: rule.name,
          type: Number(rule.type),
          minimum: Number(rule.minimum_quantity) || 0,
          collections: toIdSet(rule.product_collections),
        };
      })
      .filter(function (rule) {
        return rule.minimum > 0 && (rule.type === AMO_TYPE_AMOUNT || rule.type === AMO_TYPE_QUANTITY);
      });
  }

  /* -------------------------------------------------------------- compute */

  function lineMatchesRule(line, rule) {
    return line.collections.some(function (collection) {
      return rule.collections[String(collection.id)] === true;
    });
  }

  /**
   * Custom pricing (BSS "Custom Pricing" rules) is what assets/b2b-prices.js
   * uses to paint the wholesale price shoppers actually see per line. Minimum
   * order rules (amoRules, evaluated in this file) are a separate BSS feature
   * and know nothing about that discount, so without this the progress bar
   * compares the minimum against the full retail price while the cart lines
   * already show the discounted one. Reuse the same rule table so both agree.
   */
  function discountedLinePrice(line) {
    var prices = window.themeB2BPrices;
    if (!prices || typeof prices.readRules !== 'function') return line.linePrice;

    var rules = prices.readRules();
    if (!rules.length) return line.linePrice;

    var collectionIds = line.collections.map(function (collection) {
      return String(collection.id);
    });

    for (var i = 0; i < rules.length; i++) {
      for (var j = 0; j < collectionIds.length; j++) {
        if (rules[i].collections[collectionIds[j]]) {
          return Math.round((line.linePrice * (100 - rules[i].percent)) / 100);
        }
      }
    }
    return line.linePrice;
  }

  /**
   * For one minimum rule, total up only the cart lines it governs and report
   * how far the shopper still is from clearing it.
   */
  function evaluateRule(rule, lines) {
    var matched = lines.filter(function (line) {
      return lineMatchesRule(line, rule);
    });
    if (!matched.length) return null;

    var isAmount = rule.type === AMO_TYPE_AMOUNT;
    var current = matched.reduce(function (sum, line) {
      // BSS compares amounts in the shop currency, not cents.
      return sum + (isAmount ? discountedLinePrice(line) / 100 : line.quantity);
    }, 0);

    var scope = [];
    // First collection this rule governs that is actually in the cart — the
    // per-row "seguir comprando" link points here so the shopper lands on the
    // collection they still need to add from.
    var scopeUrl = '';
    matched.forEach(function (line) {
      line.collections.forEach(function (collection) {
        if (rule.collections[String(collection.id)] && scope.indexOf(collection.title) === -1) {
          scope.push(collection.title);
          if (!scopeUrl && collection.url) scopeUrl = collection.url;
        }
      });
    });

    return {
      rule: rule,
      isAmount: isAmount,
      current: current,
      missing: Math.max(rule.minimum - current, 0),
      met: current >= rule.minimum,
      progress: rule.minimum > 0 ? Math.min(current / rule.minimum, 1) : 1,
      scope: scope,
      scopeUrl: scopeUrl,
    };
  }

  /**
   * Per-rule custom copy, keyed by the exact rule name configured in the BSS
   * admin (Settings > Advanced Minimum Order). Overrides the generic
   * minAmount/minQuantity locale templates for that one rule, so distinct
   * wholesale tiers can read differently from each other instead of sharing
   * one sentence. Any rule not listed here keeps falling back to the generic
   * templates below, so renaming or adding a rule in BSS never breaks the
   * bar — it just loses the custom wording until this map is updated too.
   */
  var RULE_MESSAGES = {
    'Mayoristas - $20': 'El monto de tu pedido debe ser mayor o igual a %%minimum%%.',
    'Mayoristas - $200': 'El monto de tu pedido debe ser mayor o igual a %%minimum%%.',
    'Mayoristas - 2 piezas': 'Debes elegir al menos %%minimum%% piezas.',
    'Mayoristas - 3 piezas': 'Debes elegir al menos %%minimum%% piezas.',
  };

  function customRuleMessage(entry, moneyFormat) {
    var template = RULE_MESSAGES[entry.rule.name];
    if (!template) return null;
    return interpolate(template, {
      minimum: entry.isAmount
        ? formatMoney(entry.rule.minimum * 100, moneyFormat)
        : String(entry.rule.minimum),
      scope: entry.scope.join(', '),
    });
  }

  /**
   * BSS minimum-order rules carry no icon of their own, so the closest thing
   * to "the right icon for this goal" is the name of the collection(s) it
   * governs. Keyword match against the rule's own scope (never against
   * unrelated cart contents), first keyword wins; falls back to a generic
   * bag/package by rule type when nothing in the scope matches.
   */
  var SCOPE_ICON_KEYWORDS = [
    ['anillo', 'ring'],
    ['corona', 'crown'],
    ['personalizad', 'medal'],
    ['grado', 'medal'],
    ['acero', 'acero'],
  ];

  function iconForEntry(entry) {
    var haystack = entry.scope.join(' ').toLowerCase();
    for (var i = 0; i < SCOPE_ICON_KEYWORDS.length; i++) {
      if (haystack.indexOf(SCOPE_ICON_KEYWORDS[i][0]) !== -1) return SCOPE_ICON_KEYWORDS[i][1];
    }
    return entry.isAmount ? 'bag' : 'package';
  }

  /**
   * One row per wholesale minimum-order rule, met or not — each governs a
   * different set of collections, so "3.35 / 20.00" only makes sense next to
   * its own goal. Callers split pending vs. completed to decide what shows
   * by default and what sits behind the "ver mis progresos" toggle.
   */
  function buildRuleRows(context, rules) {
    var evaluated = rules
      .map(function (rule) {
        return evaluateRule(rule, context.lines);
      })
      .filter(Boolean);

    if (!evaluated.length) return null;

    var rows = evaluated.map(function (entry) {
      var unit = entry.isAmount ? '' : ' ' + context.strings.piecesUnit;
      var text = entry.met
        ? (entry.isAmount
            ? interpolate(context.strings.minAmountMet, {
                minimum: formatMoney(entry.rule.minimum * 100, context.moneyFormat),
                scope: entry.scope.join(', '),
              })
            : interpolate(context.strings.minQuantityMet, {
                minimum: String(entry.rule.minimum),
                scope: entry.scope.join(', '),
              }))
        : customRuleMessage(entry, context.moneyFormat) ||
          (entry.isAmount
            ? interpolate(context.strings.minAmount, {
                amount: formatMoney(entry.missing * 100, context.moneyFormat),
                minimum: formatMoney(entry.rule.minimum * 100, context.moneyFormat),
                scope: entry.scope.join(', '),
              })
            : interpolate(context.strings.minQuantity, {
                count: String(Math.ceil(entry.missing)),
                minimum: String(entry.rule.minimum),
                scope: entry.scope.join(', '),
              }));

      return {
        // Not rendered — render() reads it to promote the rule that governs
        // the most recently added cart line to the top of the list.
        ruleId: entry.rule.id,
        met: entry.met,
        icon: iconForEntry(entry),
        text: text,
        url: entry.scopeUrl,
        progress: entry.progress,
        current: entry.isAmount
          ? formatMoney(entry.current * 100, context.moneyFormat)
          : String(Math.min(Math.round(entry.current), entry.rule.minimum)),
        target: entry.isAmount ? formatMoney(entry.rule.minimum * 100, context.moneyFormat) : String(entry.rule.minimum) + unit,
      };
    });

    return {
      pending: rows.filter(function (row) {
        return !row.met;
      }),
      completed: rows.filter(function (row) {
        return row.met;
      }),
    };
  }

  /**
   * Free-shipping goal, computed from the B2B-discounted total rather than
   * cart.total_price so it agrees with what the cart lines actually show.
   * Shaped exactly like a buildRuleRows() row so render() can fold it into
   * the same shared list. Unlike the minimum-order rules it never blocks
   * checkout, so render() orders it after every pending mandatory row and
   * ruleRowHtml() paints it neutral (`tone`), not error-red, while pending.
   */
  function buildShippingRow(context) {
    var threshold = context.freeShippingThreshold;
    if (!threshold) return null;

    var total = context.lines.reduce(function (sum, line) {
      return sum + discountedLinePrice(line);
    }, 0);
    var met = total >= threshold;

    return {
      met: met,
      tone: met ? 'success' : 'neutral',
      icon: 'freeShipping',
      // No "seguir comprando" link on this row: free shipping isn't tied to a
      // single collection, and it's a perk rather than a blocker — the shopper
      // has nowhere specific to be sent. Rule rows keep their per-collection link.
      text: met
        ? context.strings.freeShippingMet
        : interpolate(context.strings.almostFreeShipping, {
            amount: formatMoney(threshold - total, context.moneyFormat),
          }),
      progress: Math.min(total / threshold, 1),
      current: formatMoney(total, context.moneyFormat),
      target: formatMoney(threshold, context.moneyFormat),
    };
  }

  /* --------------------------------------------------------------- render */

  function ruleRowHtml(row, icons, strings) {
    // Distinct colors on the icon, the bar fill and the label, not just a
    // checkmark — a shopper skimming the collapsed list has to tell the
    // states apart at a glance. Three tones per the business spec:
    //   error   🔴 pending mandatory condition — blocks checkout
    //   neutral ⚪ free-shipping progress while pending — a perk, not a blocker
    //   success 🟢 condition already cleared
    var tone = row.tone || (row.met ? 'success' : 'error');
    var stateVar =
      tone === 'success'
        ? '--color-success'
        : tone === 'neutral'
          ? '--colors-text'
          : '--color-error';
    return (
      '<div class="flex gap-3 items-start">' +
      '<div class="w-9 h-9 shrink-0 rounded-full bg-[rgb(var(--colors-background-secondary))] flex items-center justify-center">' +
      '<div class="w-5 h-5" style="color: rgb(var(' +
      stateVar +
      '))">' +
      (icons[row.icon] || '') +
      '</div>' +
      '</div>' +
      '<div class="flex-1 min-w-0">' +
      '<p class="text-[95%] leading-snug flex items-center gap-1.5" style="color: rgb(var(' +
      stateVar +
      '))">' +
      (row.met ? '<span class="w-4 h-4 shrink-0">' + (icons.check || '') + '</span>' : '') +
      '<span>' +
      row.text +
      '</span>' +
      '</p>' +
      '<div class="w-full mt-2 relative h-1.5 rounded-full bg-[rgb(var(--colors-line-and-border))] overflow-hidden">' +
      '<div class="absolute top-0 h-1.5 left-0 rtl:left-auto rtl:right-0 rounded-full transition-all duration-1000 ease-out" style="width: ' +
      Math.round(row.progress * 100) +
      '%; background-color: rgb(var(' +
      stateVar +
      '))"></div>' +
      '</div>' +
      '<div class="flex justify-between items-center gap-3 mt-1 text-[85%]">' +
      ctaHtml(row, strings) +
      '<span class="opacity-70 whitespace-nowrap">' +
      row.current +
      ' / ' +
      row.target +
      '</span>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /**
   * Per-row "seguir comprando" link. Points at the collection the row's rule
   * governs (row.url) so the shopper lands where they can still add stock for
   * that exact condition; falls back to nothing when the row carries no URL
   * (e.g. the shipping row with no configured target) so the footer just
   * keeps the amount, right-aligned as before.
   */
  function ctaHtml(row, strings) {
    var label = strings && strings.continueShopping;
    if (!label || !row.url) return '<span></span>';
    return (
      '<a href="' +
      row.url +
      '" class="underline hover:no-underline shrink-0" style="color: rgb(var(--colors-text-link))">' +
      label +
      '</a>'
    );
  }

  /**
   * Shows/hides the rows behind the toggle and swaps its label / chevron /
   * aria-expanded. Reused by the click handler and by render(), which always
   * starts collapsed now — the lead row alone is visible until the shopper
   * opens "ver mis progresos".
   */
  function setRuleMessagesExpanded(container, expanded) {
    var completed = container.querySelector('[data-b2b-rule-completed]');
    // max-height + opacity, not the `hidden` attribute or `display`: both
    // jump instantly with no way to transition, and `display` fights
    // Tailwind's `flex` class on this element for cascade priority (same
    // specificity, `flex` was winning and the row never actually hid). The
    // Liquid markup carries the `overflow-hidden transition-all` classes;
    // `max-height` needs the real pixel height measured at toggle time since
    // CSS can't transition to/from `auto`.
    if (completed) {
      completed.style.maxHeight = expanded ? completed.scrollHeight + 'px' : '0px';
      completed.style.opacity = expanded ? '1' : '0';
    }
    var showLabel = container.querySelector('[data-b2b-rule-toggle-show]');
    var hideLabel = container.querySelector('[data-b2b-rule-toggle-hide]');
    if (showLabel) showLabel.hidden = expanded;
    if (hideLabel) hideLabel.hidden = !expanded;
    // Keep the disclosure affordances in sync: aria-expanded for assistive
    // tech, the chevron flip for everyone else.
    var toggleBtn = container.querySelector('[data-b2b-rule-toggle]');
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var chevron = container.querySelector('[data-b2b-rule-toggle-chevron]');
    if (chevron) chevron.style.transform = expanded ? 'rotate(180deg)' : 'rotate(0deg)';
  }

  /**
   * Collapse and empty the row list. Called on every render() bail so a bar
   * that was populated on a previous pass can't survive as stale content when
   * render() runs again against a node the AJAX cart re-render left in place
   * (the 422 quantity fallback, a section-id mismatch) or before the BSS rules
   * are available.
   */
  function hideRuleMessages(root) {
    var container = root.querySelector('[data-b2b-rule-messages]');
    if (!container) return;
    container.hidden = true;
    var pendingEl = container.querySelector('[data-b2b-rule-pending]');
    var completedEl = container.querySelector('[data-b2b-rule-completed]');
    if (pendingEl) pendingEl.innerHTML = '';
    if (completedEl) completedEl.innerHTML = '';
    var toggle = container.querySelector('[data-b2b-rule-toggle]');
    if (toggle) toggle.hidden = true;
  }

  function render(root) {
    var script = root.querySelector('[data-b2b-cart-context]');
    if (!script) return;

    var context;
    try {
      context = JSON.parse(script.textContent);
    } catch (error) {
      hideRuleMessages(root);
      return;
    }
    if (!context.lines || !context.lines.length) {
      hideRuleMessages(root);
      return;
    }

    // Tags come from the live session, never from this blob: the blob is
    // re-rendered with its section, and a stale or customer-less section
    // render would otherwise silently demote a wholesale shopper to retail.
    var customerTags = window.themeB2BSession ? window.themeB2BSession.tags : [];
    if (!customerTags.length) {
      hideRuleMessages(root);
      return;
    }

    var rules = readMinimumRules(customerTags);
    if (!rules.length) {
      hideRuleMessages(root);
      return;
    }

    var icons = context.icons || {};
    var strings = context.strings || {};
    var ruleRows = buildRuleRows(context, rules);
    var shippingRow = buildShippingRow(context);

    // The old Liquid-rendered free-shipping banner/bar is retail-only from
    // here on: this shopper gets the unified list below instead, which folds
    // shipping in as just another condition, so the old markup would only
    // ever show stale/duplicate info alongside it.
    var legacyMessages = root.querySelector('[data-b2b-bar-messages]');
    if (legacyMessages) legacyMessages.style.display = 'none';
    var legacySharedBar = root.querySelector('[data-b2b-shared-bar]');
    if (legacySharedBar) legacySharedBar.style.display = 'none';

    // One shared list, ordered by how much each condition stands between the
    // shopper and checkout (business spec), top to bottom:
    //   1. pending minimum-order rows — the ones that actually block the
    //      order — closest to its goal first, since that's the quickest win
    //      and the clearest "do this next" (the cart context carries no
    //      add-order timestamp, so most-progress is the deterministic proxy);
    //   2. free-shipping progress while still pending — a perk, never a
    //      blocker, so it sits after every mandatory row and renders neutral;
    //   3. everything already cleared, in green (met free shipping last).
    // Collapsed, only row 1 shows; rows 2..n sit behind "ver mis progresos".
    var pending = (ruleRows ? ruleRows.pending : []).slice().sort(function (a, b) {
      return b.progress - a.progress;
    });
    var completed = (ruleRows ? ruleRows.completed : []).slice();

    var ordered = pending.slice();
    if (shippingRow && !shippingRow.met) ordered.push(shippingRow);
    ordered = ordered.concat(completed);
    if (shippingRow && shippingRow.met) ordered.push(shippingRow);

    // Override the lead with the pending rule governing the last product the
    // shopper added — their most recent action is what they want feedback on,
    // even when another rule sits closer to its goal. If that product falls
    // under several pending rules, the least advanced of them leads (the
    // clearest "you still need this"). No recent add, added line already gone,
    // or it matches no pending rule → the progress order above stands.
    var lastAddedId = readLastAdded();
    if (lastAddedId && pending.length) {
      var lastLine = null;
      for (var li = 0; li < context.lines.length; li++) {
        if (String(context.lines[li].variantId) === String(lastAddedId)) {
          lastLine = context.lines[li];
          break;
        }
      }
      if (lastLine) {
        var lastLineRuleIds = rules
          .filter(function (rule) {
            return lineMatchesRule(lastLine, rule);
          })
          .map(function (rule) {
            return String(rule.id);
          });
        var leadOverride = pending
          .filter(function (row) {
            return lastLineRuleIds.indexOf(String(row.ruleId)) !== -1;
          })
          .sort(function (a, b) {
            return a.progress - b.progress;
          })[0];
        if (leadOverride) {
          ordered = [leadOverride].concat(
            ordered.filter(function (row) {
              return row !== leadOverride;
            })
          );
        }
      }
    }

    var ruleContainer = root.querySelector('[data-b2b-rule-messages]');
    if (ruleContainer) {
      if (!ordered.length) {
        ruleContainer.hidden = true;
      } else {
        // `[data-b2b-rule-pending]` holds the always-visible lead row,
        // `[data-b2b-rule-completed]` the collapsible remainder — the
        // attribute names predate the reorder and are just DOM hooks now.
        var lead = ordered.slice(0, 1);
        var rest = ordered.slice(1);

        var pendingEl = ruleContainer.querySelector('[data-b2b-rule-pending]');
        var completedEl = ruleContainer.querySelector('[data-b2b-rule-completed]');
        var toggle = ruleContainer.querySelector('[data-b2b-rule-toggle]');
        if (pendingEl) {
          pendingEl.innerHTML = lead
            .map(function (row) {
              return ruleRowHtml(row, icons, strings);
            })
            .join('');
        }
        if (completedEl) {
          completedEl.innerHTML = rest
            .map(function (row) {
              return ruleRowHtml(row, icons, strings);
            })
            .join('');
        }
        if (toggle) toggle.hidden = !rest.length;
        setRuleMessagesExpanded(ruleContainer, false);
        ruleContainer.hidden = false;
      }
    }
  }

  // Delegated so it keeps working after the AJAX cart re-render swaps the bar
  // node wholesale (see watch() below) — no per-render listener to reattach.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-b2b-rule-toggle]');
    if (!toggle) return;
    var container = toggle.closest('[data-b2b-rule-messages]');
    if (!container) return;
    var completed = container.querySelector('[data-b2b-rule-completed]');
    var isCollapsed = completed ? completed.style.maxHeight === '0px' : true;
    setRuleMessagesExpanded(container, isCollapsed);
  });

  // The BSS app embed injects window.BSS_B2B from its own deferred script, which
  // can land after this one on a cold load or right after an AJAX cart
  // re-render. Without the rules there is nothing to compute, so retry a bounded
  // number of times rather than leaving the shopper looking at a stale bar.
  var BSS_RETRY_MS = 400;
  var BSS_MAX_RETRIES = 10;
  var bssRetries = 0;

  function renderAll() {
    var bars = document.querySelectorAll('[data-b2b-bar]');
    if (!bars.length) return;
    if (!bssRoot() && bssRetries < BSS_MAX_RETRIES) {
      bssRetries += 1;
      setTimeout(scheduleRender, BSS_RETRY_MS);
      return;
    }
    bars.forEach(function (bar) {
      render(bar);
    });
    syncGenericContinueShopping();
  }

  /**
   * The cart drawer and cart page each carry a generic "seguir comprando"
   * link (`[data-continue-shopping]` in the Liquid). Once the wholesale rows
   * are on screen every row has its own collection-specific link, so hide the
   * generic one to avoid the duplicate; retail shoppers (no rows) keep it.
   */
  function syncGenericContinueShopping() {
    var hasRows = !!document.querySelector('[data-b2b-rule-messages]:not([hidden])');
    document.querySelectorAll('[data-continue-shopping]').forEach(function (el) {
      el.style.display = hasRows ? 'none' : '';
    });
  }

  // Coalesce the observer, the cart event and the BSS retry into one pass per
  // frame — a single item removal can fire several of them back to back.
  var renderQueued = false;
  function scheduleRender() {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(function () {
      renderQueued = false;
      renderAll();
    });
  }

  /*
   * The cart drawer and cart page are re-rendered over AJAX, which replaces the
   * bar node wholesale, so re-run whenever a new bar shows up. The MutationObserver
   * covers that, but it is the only trigger: paths that patch the cart without
   * swapping the bar node (the 422 quantity fallback, a section-id mismatch in
   * the theme's re-render) would otherwise leave the bar frozen. Listening to the
   * theme's own cart event — the same hook assets/b2b-min-order-guard.js uses —
   * makes the recompute fire on every cart mutation regardless of DOM shape.
   */
  function watch() {
    scheduleRender();

    var observer = new MutationObserver(function (mutations) {
      var needsRender = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return (
            node.nodeType === 1 &&
            (node.hasAttribute('data-b2b-bar') || node.querySelector('[data-b2b-bar]'))
          );
        });
      });
      if (needsRender) scheduleRender();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('eurus:cart:items-changed', scheduleRender);
    document.addEventListener('eurus:product:added', function (event) {
      rememberLastAdded(event.detail);
      scheduleRender();
    });

    // Signing in or out under an already-rendered document changes which rules
    // apply — including down to none, which renderAll resolves by hiding the
    // rows again.
    if (window.themeB2BSession) window.themeB2BSession.onChange(scheduleRender);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  // Exposed so the logic can be exercised without a wholesale login, and so
  // assets/b2b-min-order-guard.js can reuse the same rule matching instead
  // of re-implementing it against a hypothetical cart line.
  window.themeB2BCartProgress = {
    buildRuleRows: buildRuleRows,
    buildShippingRow: buildShippingRow,
    evaluateRule: evaluateRule,
    readMinimumRules: readMinimumRules,
    discountedLinePrice: discountedLinePrice,
    formatMoney: formatMoney,
    interpolate: interpolate,
    customRuleMessage: customRuleMessage,
  };
})();
